import React, { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import DataTable, { Column } from '../../components/DataTable/DataTable';
import { CrewDataTableFrame, CrewDataTablePrompt, CrewReportViewAction, useCrewReportOpen } from './reportShared';
import { DateInput, VesselSelect } from '../../components/ui';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import api from '../../services/api';
import CrewReportSearchHeader, { CrewReportFilterField } from './CrewReportSearchHeader';
import type { OfficerMatrixResponse, OfficerMatrixRow } from './types';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

type MatrixEvalResult = {
  ruleId: number;
  kind: string;
  label: string;
  status: 'pass' | 'warning' | 'fail' | 'notReady';
  detail?: string;
  actual?: number | null;
  minimum?: number | null;
  persons?: { crewId: number; name: string; value: number }[];
};

type MatrixEvalResponse = {
  results: MatrixEvalResult[];
  matrix: { id: number; name: string } | null;
};

const TextCell: React.FC<{ value: string | number | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  const text = value == null || value === '' ? '-' : String(value);
  return <span className={cn('text-sm whitespace-nowrap', colors.text)}>{text}</span>;
};

function statusClass(status: MatrixEvalResult['status']) {
  if (status === 'pass') return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
  if (status === 'warning') return 'bg-amber-500/15 text-amber-800 dark:text-amber-300';
  if (status === 'fail') return 'bg-red-500/15 text-red-700 dark:text-red-300';
  return 'bg-slate-500/15 text-slate-600 dark:text-slate-300';
}

const OfficerMatrixPage: React.FC = () => {
  const { colors } = useTheme();
  const { listReturnPath, openCrew } = useCrewReportOpen();
  const [vesselId, setVesselId] = useState('');
  const [periodFrom, setPeriodFrom] = useState(todayIso);
  const [periodTo, setPeriodTo] = useState(todayIso);
  const [applied, setApplied] = useState<{
    vesselId: string;
    periodFrom: string;
    periodTo: string;
  } | null>(null);
  const [evalResults, setEvalResults] = useState<MatrixEvalResult[]>([]);
  const [evalLoading, setEvalLoading] = useState(false);

  const listUrl = applied
    ? `/crew/officer-matrix?vesselId=${encodeURIComponent(applied.vesselId)}&periodFrom=${encodeURIComponent(applied.periodFrom)}&periodTo=${encodeURIComponent(applied.periodTo)}`
    : null;

  const { data, error, isLoading, mutate } = useSWR<OfficerMatrixResponse>(listUrl, fetcher, {
    revalidateOnFocus: false,
  });

  const loadEval = useCallback(async (vId: string, asOf: string) => {
    setEvalLoading(true);
    try {
      const res = await api.post<MatrixEvalResponse>('/crew-manning-matrix/evaluate', {
        vesselId: Number(vId),
        asOf,
        swaps: [],
      });
      setEvalResults(res.data?.results ?? []);
    } catch {
      setEvalResults([]);
    } finally {
      setEvalLoading(false);
    }
  }, []);

  const handleSearch = useCallback(() => {
    if (!vesselId) return;
    setApplied({ vesselId, periodFrom, periodTo });
    void loadEval(vesselId, periodTo);
  }, [vesselId, periodFrom, periodTo, loadEval]);

  const rows = data?.rows ?? [];
  const vessel = data?.vessel;
  const pairResults = evalResults.filter(
    (r) => r.kind === 'COMBINED' || r.kind === 'TOGETHER' || r.kind === 'STAGGER',
  );

  const columns: Column<OfficerMatrixRow>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Name',
        sortable: true,
        render: (_v, row) => (
          <Link
            to={`/crew/${row.crewId}`}
            state={{ from: listReturnPath }}
            className={cn('text-sm hover:underline whitespace-nowrap', colors.primaryFg)}
            onClick={(e) => e.stopPropagation()}
          >
            {row.name}
          </Link>
        ),
      },
      { key: 'rank', header: 'Rank', sortable: true, render: (v) => <TextCell value={v} /> },
      { key: 'nation', header: 'Nation', sortable: true, render: (v) => <TextCell value={v} /> },
      { key: 'certComp', header: 'Cert. Comp', sortable: true, render: (v) => <TextCell value={v} /> },
      {
        key: 'issuingCountry',
        header: 'Issuing Country',
        sortable: true,
        render: (v) => <TextCell value={v} />,
      },
      { key: 'tankerCert', header: 'Tanker Cert.', sortable: true, render: (v) => <TextCell value={v} /> },
      {
        key: 'specialisedTankerTraining',
        header: 'Specialised tanker Training',
        sortable: true,
        render: (v) => <TextCell value={v} />,
      },
      { key: 'radioQual', header: 'Radio Qual.', sortable: true, render: (v) => <TextCell value={v} /> },
      {
        key: 'yearsWithOperator',
        header: 'Years with Operator',
        sortable: true,
        render: (v) => <TextCell value={v} />,
      },
      {
        key: 'yearsInRank',
        header: 'Years in Rank',
        sortable: true,
        render: (v) => <TextCell value={v} />,
      },
      {
        key: 'yearsInRankWithOperator',
        header: 'Years in Rank with Operator',
        sortable: true,
        render: (v) => <TextCell value={v} />,
      },
      {
        key: 'yearsSameVesselType',
        header: 'Years in Same vessel type',
        sortable: true,
        render: (v) => <TextCell value={v} />,
      },
      {
        key: 'yearsSimilarVesselType',
        header: 'Years in Similar vessel type',
        sortable: true,
        render: (v) => <TextCell value={v} />,
      },
      {
        key: 'monthsOnbThisVessel',
        header: 'Months ONB on this vessel',
        sortable: true,
        render: (v) => <TextCell value={v} />,
      },
    ],
    [colors.primaryFg, listReturnPath],
  );

  return (
    <div className="h-full flex flex-col min-h-0 gap-3">
      <CrewReportSearchHeader
        hint={{
          title: "Officer's Qualification Matrix",
          body: 'Lists officers on the selected vessel for the period with certificate of competency, issuing country, and years of experience by rank and vessel type. Use this to review qualification coverage for manning.',
        }}
        onSearch={handleSearch}
        searchDisabled={!vesselId}
        loading={isLoading}
        onRefresh={
          applied
            ? () => {
                void mutate();
                void loadEval(applied.vesselId, applied.periodTo);
              }
            : undefined
        }
        context={
          vessel
            ? `Officer matrix · ${vessel.vesselName}${vessel.vesselAbrv ? ` (${vessel.vesselAbrv})` : ''} · ${applied?.periodFrom} → ${applied?.periodTo}`
            : undefined
        }
      >
        <CrewReportFilterField label="Vessel" htmlFor="officer-matrix-vessel" width="lg">
          <VesselSelect
            name="vesselId"
            value={vesselId}
            onChange={(e) => setVesselId(String(e.target.value))}
            placeholder="Select vessel…"
            includeIds={vesselId ? [vesselId] : undefined}
          />
        </CrewReportFilterField>
        <CrewReportFilterField label="Period from" htmlFor="officer-matrix-from" width="md">
          <DateInput
            id="officer-matrix-from"
            name="periodFrom"
            value={periodFrom}
            onChange={(e) => setPeriodFrom(e.target.value)}
          />
        </CrewReportFilterField>
        <CrewReportFilterField label="Period to" htmlFor="officer-matrix-to" width="md">
          <DateInput
            id="officer-matrix-to"
            name="periodTo"
            value={periodTo}
            onChange={(e) => setPeriodTo(e.target.value)}
          />
        </CrewReportFilterField>
      </CrewReportSearchHeader>

      <CrewDataTableFrame>
        {!applied ? (
          <CrewDataTablePrompt>Select a vessel and period, then search.</CrewDataTablePrompt>
        ) : error ? (
          <CrewDataTablePrompt>
            <span className="text-red-600">Could not load officer matrix.</span>
          </CrewDataTablePrompt>
        ) : (
          <DataTable<OfficerMatrixRow>
            data={rows}
            columns={columns}
            keyExtractor={(row) => row.transactionId}
            loading={isLoading}
            emptyMessage="No data to display"
            onRowClick={(row) => openCrew(row.crewId)}
            actions={(row) => <CrewReportViewAction crewId={row.crewId} from={listReturnPath} />}
            actionsPosition="left"
          />
        )}
      </CrewDataTableFrame>

      {applied && (
        <div
          className={cn(
            'rounded-xl border px-4 py-3 text-sm shadow-sm space-y-2',
            colors.surface,
            colors.border,
          )}
        >
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <span className={colors.textSecondary}>Officers</span>
              <span className={cn('ml-2 font-semibold', colors.text)}>
                {data?.summary?.crewCount ?? 0}
              </span>
            </div>
            <div className={cn('text-xs', colors.textSecondary)}>
              Manning matrix pairs{evalLoading ? ' (loading…)' : ''}
            </div>
          </div>
          {pairResults.length === 0 && !evalLoading ? (
            <p className={cn('text-xs', colors.textSecondary)}>
              No pair rules for this vessel. Configure them under Parameters → Crew Matrix Rules.
            </p>
          ) : (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {pairResults.map((r) => (
                <div key={r.ruleId} className={cn('rounded-lg border px-3 py-2', colors.border)}>
                  <div className="flex items-start justify-between gap-2">
                    <span className={cn('text-sm font-medium', colors.text)}>{r.label}</span>
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
                        statusClass(r.status),
                      )}
                    >
                      {r.status}
                    </span>
                  </div>
                  <p className={cn('mt-1 text-xs', colors.textSecondary)}>{r.detail}</p>
                  {r.persons && r.persons.length > 0 && (
                    <ul className={cn('mt-1 space-y-0.5 text-xs', colors.textMuted)}>
                      {r.persons.map((p) => (
                        <li key={`${r.ruleId}-${p.crewId}`}>
                          {p.name}: {p.value}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default OfficerMatrixPage;
